import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { CompanyInfo } from 'src/app/shared/models/company';

export class SalesTaxPdfGenerator {
  generatePdf(data: any, companyInfo: CompanyInfo, period: string, startDate: string, endDate: string): jsPDF {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 40;
    let yPosition = 40;

    // Header - Company Name
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(companyInfo?.company_name || 'Sales Tax Report', margin, yPosition);
    yPosition += 25;

    // Report Subtitle
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Sales Tax Liability Statement', margin, yPosition);
    
    // Period Label
    const periodStr = period === 'Custom' ? `${startDate} to ${endDate}` : period;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Period: ${periodStr}`, margin, yPosition + 15);
    
    yPosition += 50;

    // Summary Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text('Liability Summary', margin, yPosition);
    yPosition += 15;

    const summaryRows = [
      ['Total Gross Sales', `$${(data.totalGrossSales || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Total Non-Taxable Sales', `$${(data.totalNonTaxable || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Total Taxable Sales', `$${(data.totalTaxable || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`],
      ['Total Sales Tax Collected', `$${(data.totalTaxCollected || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [['Metric', 'Amount']],
      body: summaryRows,
      margin: { left: margin },
      styles: { fontSize: 10 },
      headStyles: { fillColor: [44, 62, 80] },
      columnStyles: {
        1: { halign: 'right', fontStyle: 'bold' }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 40;

    // Detailed Breakdown by Jurisdiction
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Tax Breakdown by Jurisdiction', margin, yPosition);
    yPosition += 15;

    const breakdownRows = data.jurisdictions.map((row: any) => [
      row.name,
      `${(row.rate || 0).toFixed(2)}%`,
      `$${(row.grossSales || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
      `$${(row.taxable || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
      `$${(row.taxCollected || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
    ]);

    // Add Totals row
    breakdownRows.push([
      'TOTAL',
      '',
      `$${(data.totalGrossSales || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
      `$${(data.totalTaxable || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
      `$${(data.totalTaxCollected || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Jurisdiction', 'Rate', 'Gross Sales', 'Taxable Sales', 'Tax Collected']],
      body: breakdownRows,
      margin: { left: margin },
      styles: { fontSize: 9 },
      headStyles: { fillColor: [255, 152, 0] },
      columnStyles: {
        1: { halign: 'center' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right', fontStyle: 'bold' }
      },
      didParseCell: (dataCell) => {
        if (dataCell.row.index === breakdownRows.length - 1) {
          dataCell.cell.styles.fontStyle = 'bold';
          dataCell.cell.styles.fillColor = [240, 240, 240];
        }
      }
    });

    // Footer
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Generated on ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'center' }
      );
    }

    return doc;
  }
}
